import React, { useState, useEffect, useMemo } from "react";
import ButtonComponent from "../components/Buttons/button";
import { CustomInput } from "../components/InputFields/CustomInput";
import { useLocation, useNavigate } from "react-router-dom";
import CustomCheckbox from "../components/InputFields/CustomCheckbox";
import constants from "../utils/constants";
import api from "../services/api";
import { CustomDropdown } from "../components/InputFields/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { fieldValidator, formValidator } from "../utils/validations";
import {
  hideLoaderAction,
  showLoaderAction,
} from "../redux/actions/loaderAction";
import TransactionSuccessModal from "../components/Models/TransactionSuccessModal";
import { showToast } from "../redux/actions/toastAction";
import {
  getNetworkFee,
  validateWalletAddress,
} from "../redux/actions/transactionsAction";
import { validEmail } from "../utils/regex/regex";
import { getGasFeesCurrencyList } from "../redux/actions/gasFeesAction";
import { getActiveNetworkGasFees } from "../redux/actions/networkGasFeeAction";
import { getAdminDetails, getUserInfo } from "../redux/actions/userActions";
import useDebounce from "../utils/hooks";
import TransactionSuccess from "../components/TransactionSuccess/TransactionSuccess";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [walletAddress, setWalletAddress] = useDebounce(500, "");

  const location = useLocation();

  const [isKYCVerified, setIsKYCVerified] = useState(null);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const [successData, setSuccessData] = useState({});

  let { isValidWalletAddress } = useSelector((state) => state.transactions);

  let { gasFees } = useSelector((state) => state.gasFees);
  let { networkGasFees: activeNetworks } = useSelector((state) => state.networkGasFees);

  const { userDetails, adminDetails } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(getGasFeesCurrencyList());
    dispatch(getActiveNetworkGasFees());
    dispatch(getAdminDetails());
    dispatch(getUserInfo());
  }, [dispatch]);

  // Derive dropdown options from the active-network list returned by the
  // backend. Falls back to the static list while the network list is loading.
  const networkOptions = useMemo(() => {
    if (!activeNetworks || activeNetworks.length === 0) {
      return constants.networkOptions;
    }
    return activeNetworks.map((n) => ({ name: n.label, value: n.network }));
  }, [activeNetworks]);

  const isNetworkActive = (networkValue) => {
    if (!activeNetworks || activeNetworks.length === 0) return true; // loading → don't block
    return activeNetworks.some((n) => n.network === networkValue);
  };

  const checkUserKYCVerification = async () => {
    const res = await api("get", constants.endPoints.checkKYCVerification);
    if (res.success) {
      setIsKYCVerified(res.data.kycVerified);
    } else {
      console.log(res.message);
    }
  };

  const [confirmWalletOwner, setConfirmWalletOwner] = useState(false);
  const [transactionSuccessModal, setTransactionSuccessModal] = useState(false);

  const cryptoCurrencies = constants.cryptoCurrencies;

  const [transactionForm, setTransactionFormData] = useState({
    walletAddress: "",
    network: "",
    email: userDetails?.email,
    gasFees: "",
    amountPaid: location.state ? location.state.amountPaid.toString() : "",
    paidAmountCurrency: location.state
      ? location.state.paidAmountCurrency
      : "USDT",
    amountReceived: location.state
      ? location.state.amountReceived.toString()
      : "",
    receivedAmountCurrency: location.state
      ? location.state.receivedAmountCurrency
      : "CAD",
  });

  useEffect(() => {
    if (
      transactionForm.walletAddress &&
      transactionForm.paidAmountCurrency &&
      transactionForm.receivedAmountCurrency &&
      transactionForm.network
    ) {
      const data = {
        walletAddress: transactionForm.walletAddress,
        currency:
          transactionForm.paidAmountCurrency === "CAD"
            ? transactionForm.receivedAmountCurrency
            : transactionForm.paidAmountCurrency,
        network: transactionForm.network,
      };
      dispatch(validateWalletAddress(data));
    }
  }, [walletAddress]);

  const resetTransactionForm = () => {
    setTransactionFormData({
      walletAddress: "",
      network: "",
      email: userDetails.email,
      gasFees: "",
      amountPaid: "",
      paidAmountCurrency: "USDT",
      amountReceived: "",
      receivedAmountCurrency: "CAD",
    });
  };

  const formValidationKeyLabels = {
    walletAddress: "wallet address",
    network: "network",
    // email: "email",
  };

  let [errors, setErrors] = useState({});

  const handleChange = async ({ name, value, label }) => {
    const fieldValidation = await fieldValidator(name, value, errors, label);
    setErrors({ ...fieldValidation });

    if (name === "amountPaid" && !value) transactionForm.amountReceived = "";
    if (name === "paidAmountCurrency" && value === "CAD")
      transactionForm.receivedAmountCurrency = "USDT";

    if (name === "paidAmountCurrency" && value !== "CAD")
      transactionForm.receivedAmountCurrency = "CAD";

    if (name === "walletAddress") setWalletAddress(value);

    setTransactionFormData({ ...transactionForm, [name]: value });
  };

  useEffect(() => {
    if (
      transactionForm.amountPaid &&
      transactionForm.network &&
      transactionForm.paidAmountCurrency &&
      transactionForm.receivedAmountCurrency
    ) {
      const data = {
        payCurrency: transactionForm.paidAmountCurrency,
        receiveCurrency: transactionForm.receivedAmountCurrency,
        tradeType:
          transactionForm.paidAmountCurrency === "CAD" &&
            transactionForm.receivedAmountCurrency !== "USD"
            ? "BUY"
            : "SELL",
        network: transactionForm.network,
        amount: transactionForm.amountPaid,
      };
      if (walletAddress) {
        const data = {
          walletAddress: transactionForm.walletAddress,
          currency:
            transactionForm.paidAmountCurrency === "CAD"
              ? transactionForm.receivedAmountCurrency
              : transactionForm.paidAmountCurrency,
          network: transactionForm.network,
        };
        dispatch(validateWalletAddress(data));
      }
      getGasFee(data);
    } else if (transactionForm.gasFees) {
      setTransactionFormData((prev) => ({
        ...prev,
        gasFees: 0,
      }));
    }
  }, [
    transactionForm.paidAmountCurrency,
    transactionForm.network,
    transactionForm.amountPaid,
    transactionForm.receivedAmountCurrency,
  ]);

  const handleSubmit = async () => {
    transactionForm.email = userDetails.email;
    setTransactionFormData(transactionForm);

    const formErrors = await formValidator(
      formValidationKeyLabels,
      transactionForm,
      errors
    );

    if (!validEmail.test(transactionForm.email)) {
      formErrors.email = "Enter your valid Email";
    }

    setErrors({ ...errors, ...formErrors });

    if (Object.values(formErrors).length) return;

    if (!transactionForm.amountPaid) {
      return dispatch(
        showToast({
          severity: "error",
          summary: "Please enter amount to be paid by you.",
        })
      );
    }

    if (!transactionForm.paidAmountCurrency) {
      return dispatch(
        showToast({
          severity: "error",
          summary: "Please select pay amount currency.",
        })
      );
    }

    if (!transactionForm.receivedAmountCurrency) {
      return dispatch(
        showToast({
          severity: "error",
          summary: "Please select receive amount currency.",
        })
      );
    }

    // Guard: block submission if the selected network has been deactivated
    // by the admin. We still allow the request to proceed while the active
    // list is still loading (isNetworkActive returns true in that case).
    if (!isNetworkActive(transactionForm.network)) {
      return dispatch(
        showToast({
          severity: "error",
          summary: "Selected network is currently unavailable. Please choose a different network.",
        })
      );
    }

    dispatch(showLoaderAction());
    const adminWalletAddress = adminDetails.walletAddresses?.find(
      (item) =>
        item.network.toLowerCase() === transactionForm.network.toLowerCase()
    )?.address || "";

    const res = await api("post", constants.endPoints.createTransaction, {
      ...transactionForm,
      adminWalletAddress: adminWalletAddress,
      eTransferMail: adminDetails.eTransferMail,
    });
    if (res.success) {
      setSuccessData({
        tradeType:
          transactionForm.paidAmountCurrency === "CAD" ? "BUY" : "SELL",
        amount: transactionForm.amountPaid,
        payCurrency: transactionForm.paidAmountCurrency,
        receiveCurrency: transactionForm.receivedAmountCurrency,
        userWalletAddress: transactionForm.walletAddress,
        adminWalletAddress: adminWalletAddress,
        eTransferMail: adminDetails.eTransferMail,
      });
      setIsRequestSubmitted(true);
      resetTransactionForm();
      dispatch(hideLoaderAction());
      dispatch(showToast({ severity: "success", summary: res.message }));
      // setTransactionSuccessModal(true);
    } else {
      dispatch(hideLoaderAction());
      dispatch(
        showToast({
          severity: "error",
          summary: res.message ? res.message : "Something went wrong.",
        })
      );
    }
  };

  const findFeeForPair = (payCurrency, receiveCurrency, network) => {
    if (!gasFees || !gasFees.length) return null;
    return gasFees.find((fee) =>
      fee.firstCurrency === payCurrency &&
      fee.secondCurrency === receiveCurrency &&
      fee.network === network
    );
  };

  const getGasFee = async (data) => {
    const fee = findFeeForPair(data.payCurrency, data.receiveCurrency, data.network);

    if (fee) {
      const rate = fee.selectedExchangeRate === "Market" && fee.liveMarketRate
        ? fee.liveMarketRate
        : fee.exchangePricePerUnit;

      // Per-network flat gas fee (blockchain cost)
      const networkGasFee = activeNetworks?.find(n => n.network === data.network)?.gasFee || 0;

      // Convert amount via exchange rate
      const convertedAmount = data.tradeType === "BUY"
        ? parseFloat(data.amount) / rate
        : parseFloat(data.amount) * rate;

      // Flat fees: per-network gas + per-pair gas
      const flatFees = Number(networkGasFee) + Number(fee.fees || 0);

      // Percentage fees: transaction fee + market spread
      const txnFeePercent = Number(fee.transactionFee) || 0;
      const spreadPercent = Number(fee.marketSpreadPercent) || 0;
      const percentFees = convertedAmount * ((txnFeePercent + spreadPercent) / 100);

      // Total deductions
      const totalFee = flatFees + percentFees;

      setTransactionFormData((prev) => ({
        ...prev,
        gasFees: totalFee.toFixed(2),
        amountReceived: (convertedAmount - totalFee).toFixed(2),
      }));
    } else if (data.receiveCurrency) {
      // Always send the crypto side (never CAD) for CoinGecko lookup
      const cryptoCurrency = data.payCurrency === "CAD" ? data.receiveCurrency : data.payCurrency;
      const payload = {
        currency: cryptoCurrency,
        tradeType: data.tradeType,
        network: data.network,
        amount: data.amount,
      };
      return dispatch(getNetworkFee(payload, setTransactionFormData));
    }
  };

  useEffect(() => {
    checkUserKYCVerification();
  }, []);

  return (
    <>
      <TransactionSuccessModal
        transactionSuccessModal={transactionSuccessModal}
        setTransactionSuccessModal={setTransactionSuccessModal}
      />

      <div className="dashboard-wrapper">
        <div className="dashboard-grid">
          {/* Left Column: Info & Instructions */}
          <div className="dashboard-col-left">
            {!isKYCVerified ? (
              <div className="dashboard-kyc-card">
                <h3>
                  You have not verified your KYC — please verify to start
                  trading.
                </h3>
                <ButtonComponent
                  style={{ height: "35px" }}
                  onClick={() =>
                    navigate("/kyc-verification", {
                      state: { email: sessionStorage.getItem("email") },
                    })
                  }
                  label={"Get Started"}
                />
              </div>
            ) : (
              <div className="dashboard-card how-to-swap">
                <h3>How to Buy/Sell Stablecoins on Tinbox</h3>
              </div>
            )}

            <div className="dashboard-card how-to-swap">
              <h3>How to Swap</h3>
              <p className="step-item">
                <span className="step-number">Step 1.</span> Begin by
                selecting your preferred network.
              </p>
              <p className="step-item">
                <span className="step-number">Step 2.</span> Choose the
                currencies you wish to exchange and enter the corresponding
                amounts. (Min. of $250 CAD and Max. of $10,000 per
                transaction)
              </p>
              <p className="step-item">
                <span className="step-number">Step 3.</span> Once you've
                selected the network and entered the currency amounts, the
                estimated gas fees will be displayed.
              </p>
              <p className="step-item">
                <span className="step-number">Step 4.</span> Conclude the
                process by entering your wallet address and clicking on the
                "Swap" button.
              </p>
              <p className="step-item">
                <span className="step-number">Step 5.</span> Follow the
                on-screen instructions and check your email for additional
                guidance. Wait for the approval before transferring funds.
                You can check the status under "Transaction History."
              </p>
            </div>

            <div className="help-card">
              <h4>🚀 Need Help?</h4>
              <p>
                Contact our support team for assistance with large volume
                trades.
              </p>
            </div>
          </div>

          {/* Right Column: Swap Form */}
          <div className="dashboard-col-right" style={{ position: "relative" }}>
            {!isKYCVerified && (
              <div
                className="absolute blur-div"
                style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
              ></div>
            )}
            <div className="dashboard-card">
              {isRequestSubmitted ? (
                <>
                  <div className="dashboard-close-btn">
                    <button onClick={() => setIsRequestSubmitted(false)}>
                      ✕
                    </button>
                  </div>
                  <TransactionSuccess
                    successData={successData}
                  ></TransactionSuccess>
                </>
              ) : (
                <>
                  {/* Network selector — only shows networks the admin has marked active */}
                  <div>
                    <CustomDropdown
                      label={"Network"}
                      placeholder="Select Network"
                      disabled={!isKYCVerified}
                      height={"45px"}
                      options={networkOptions}
                      optionLabel={"name"}
                      name="network"
                      value={transactionForm.network}
                      onChange={handleChange}
                    />
                    {errors?.network && (
                      <p className="field-error">{errors?.network}</p>
                    )}
                  </div>

                  {/* Wallet Information */}
                  <p className="wallet-info-label">Wallet Information</p>

                  <div className="swap-container">
                    {/* You Pay row */}
                    <div className="swap-row">
                      <div>
                        <div className="swap-label">You Pay</div>
                        <div className="mobile-inputsizee">
                          <CustomInput
                            extraClass={"border-none"}
                            disabled={!isKYCVerified}
                            type={"number"}
                            placeholder="0"
                            name="amountPaid"
                            value={transactionForm.amountPaid}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="currency-badge">
                        <p className="Customdrop" style={{ margin: 0 }}>
                          <CustomDropdown
                            className="bg-transparent"
                            disabled={!isKYCVerified}
                            options={cryptoCurrencies}
                            name="paidAmountCurrency"
                            value={transactionForm.paidAmountCurrency}
                            onChange={handleChange}
                            optionLabel={""}
                          />
                        </p>
                      </div>
                    </div>

                    {/* You Get row */}
                    <div className="swap-row">
                      <div>
                        <div className="swap-label">You Get</div>
                        <div className="mobile-inputsizee">
                          <CustomInput
                            disabled
                            type={"number"}
                            extraClass={"border-none"}
                            placeholder="0"
                            name={"amountReceived"}
                            value={transactionForm.amountReceived}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="currency-badge currency-badge--crypto">
                        <p className="Customdrop" style={{ margin: 0 }}>
                          <CustomDropdown
                            disabled={!isKYCVerified}
                            placeholder={""}
                            className="bg-transparent"
                            options={
                              transactionForm.paidAmountCurrency !== "CAD"
                                ? ["CAD"]
                                : cryptoCurrencies.filter(
                                  (item) =>
                                    item !==
                                    transactionForm.paidAmountCurrency
                                )
                            }
                            optionLabel={""}
                            name="receivedAmountCurrency"
                            value={transactionForm.receivedAmountCurrency}
                            onChange={handleChange}
                          />
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Rate info */}
                  {transactionForm.amountPaid &&
                    transactionForm.amountReceived ? (
                    <div className="rate-info">
                      <span>
                        Rate: 1 {transactionForm.paidAmountCurrency} ={" "}
                        {(Number(transactionForm.amountReceived) / Number(transactionForm.amountPaid || 1)).toFixed(4)}{" "}
                        {transactionForm.receivedAmountCurrency}
                      </span>
                      <span>Fees Included</span>
                    </div>
                  ) : null}

                  {/* Email & Gas Fees */}
                  <div className="dashboard-form-section">
                    <div className="form-row">
                      <div>
                        <CustomInput
                          label={"E-Transfer Email Address"}
                          name="email"
                          disabled
                          value={userDetails.email}
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Fees Included"
                          name="gasFees"
                          value={transactionForm.gasFees}
                          onChange={handleChange}
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  {/* Wallet Address */}
                  <CustomInput
                    label={"Wallet address"}
                    name="walletAddress"
                    value={transactionForm.walletAddress}
                    onChange={handleChange}
                    disabled={!isKYCVerified || !transactionForm.network}
                  />
                  {errors?.walletAddress && (
                    <p className="field-error">{errors?.walletAddress}</p>
                  )}

                  {/* Confirmation & Submit */}
                  <div className="dashboard-checkbox">
                    <CustomCheckbox
                      extraClass={"text-center"}
                      label={
                        "Confirm that I am the owner of this wallet"
                      }
                      value={confirmWalletOwner}
                      onChange={async () => {
                        setConfirmWalletOwner((prev) => !prev);
                      }}
                    />
                  </div>

                  <div className="dashboard-btn-swap">
                    <ButtonComponent
                      label="Swap"
                      className="w-full"
                      onClick={handleSubmit}
                      disable={
                        !confirmWalletOwner || !isValidWalletAddress
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
